import { PostBillingInfoType } from "@common/schemas/billing-info";
import { featureFlags } from "@common/feature-flags";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { Suspense, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { AuthForm } from "../components/AuthForm";
import { BillingForm } from "../components/BillingForm";
import {
  Modal,
  ModalContent,
  ModalTitle,
  useMuiModal,
} from "../components/modal";
import { PersonalForm, type PersonalDetails } from "../components/PersonalForm";
import { Button } from "../components/ui/button";
import { StepIndicator } from "../components/ui/step-indicator";
import {
  Footer,
  Header,
  Page,
  PageContent,
  PageTitle,
} from "@/components/page";
import { useShop } from "@/hooks/use-shop";
import {
  EmailVerificationForm,
  useSendEmailVerificationMutation,
  verificationErrorMessage,
} from "@/features/registration/email-verification-form";
import { AudioSlot, useAudioSlot } from "@/features/audio/kiosk-audio";

export const Route = createFileRoute("/registration-screen")({
  component: RegistrationPage,
});

type Step =
  | "personal-details"
  | "email-verification"
  | "billing-info"
  | "auth-and-contracts";

type PersonalWithUsername = PersonalDetails & { recommendedUsername: string };

interface PersonalDetailsState {
  step: "personal-details";
}

interface EmailVerificationState {
  step: "email-verification";
  personalDetails: PersonalWithUsername;
}

interface BillingInfoState {
  step: "billing-info";
  personalDetails: PersonalWithUsername;
  emailVerificationToken?: string;
}

interface AuthContractsState {
  step: "auth-and-contracts";
  personalDetails: PersonalWithUsername;
  billingInfo?: PostBillingInfoType;
  recommendedUsername?: string;
  emailVerificationToken?: string;
}

type RegistrationState =
  | PersonalDetailsState
  | EmailVerificationState
  | BillingInfoState
  | AuthContractsState;

function RegistrationPage() {
  const { showModal } = useMuiModal();
  const [state, setState] = useState<RegistrationState>({
    step: "personal-details",
  });
  const [verifiedEmail, setVerifiedEmail] = useState<{
    email: string;
    token: string;
  } | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: shop } = useShop();
  const isEur = shop.currencyCode === "EUR";
  const sendEmailVerification = useSendEmailVerificationMutation();
  const emailVerificationEnabled = featureFlags.kiosk.emailVerificationCode;

  const steps = useMemo(
    () =>
      [
        { id: "personal-details", title: t("Személyes adatok") },
        emailVerificationEnabled && {
          id: "email-verification",
          title: t("E-mail ellenőrzése"),
        },
        !isEur && { id: "billing-info", title: t("Számlázási adatok") },
        { id: "auth-and-contracts", title: t("Azonosítás és szerződések") },
      ].filter(Boolean) as { id: string; title: string }[],
    [t, isEur, emailVerificationEnabled],
  );

  const goToAfterEmail = async (
    personalDetails: PersonalWithUsername,
    emailVerificationToken: string | undefined,
  ) => {
    if (isEur) {
      await showModal(<UsernameRecommendationModal />);
      setState({
        step: "auth-and-contracts",
        personalDetails,
        emailVerificationToken,
      });
    } else {
      setState({
        step: "billing-info",
        personalDetails,
        emailVerificationToken,
      });
    }
  };

  const proceedAfterPersonal = async (
    personalDetails: PersonalWithUsername,
  ) => {
    if (!emailVerificationEnabled) {
      await goToAfterEmail(personalDetails, undefined);
      return;
    }
    if (verifiedEmail?.email === personalDetails.email) {
      await goToAfterEmail(personalDetails, verifiedEmail.token);
      return;
    }
    try {
      await sendEmailVerification.mutateAsync({
        email: personalDetails.email,
        firstName: personalDetails.firstName,
        lastName: personalDetails.lastName,
      });
    } catch (error) {
      throw new Error(
        verificationErrorMessage(
          error,
          t("Nem sikerült elküldeni az ellenőrző e-mailt. Próbáld újra."),
        ),
      );
    }
    setVerifiedEmail(null);
    setState({
      step: "email-verification",
      personalDetails,
    });
  };

  return (
    <Page>
      <Header
        backArrowHref={`/login-option-selector-screen`}
        variant={`small`}
      />
      <PageContent>
        <PageTitle>
          <Trans>Bubbles fiók regisztráció</Trans>
        </PageTitle>

        <AudioSlot slot={`page.registration.${state.step}`} />
        <StepIndicator
          steps={steps}
          currentStep={state.step}
          completedSteps={getCompletedSteps(state.step, {
            emailVerificationEnabled,
            isEur,
          })}
          className="mb-6"
        />

        {/* Current step form */}
        <div className="grid grid-cols-1 gap-y-4">
          <Suspense fallback={<Loader2 className="animate-spin w-5 h-5" />}>
            {state.step === "personal-details" && (
              <PersonalForm onNext={proceedAfterPersonal} />
            )}
            {state.step === "email-verification" && (
              <EmailVerificationForm
                email={state.personalDetails.email}
                firstName={state.personalDetails.firstName}
                lastName={state.personalDetails.lastName}
                onVerified={(token) => {
                  setVerifiedEmail({
                    email: state.personalDetails.email,
                    token,
                  });
                  void goToAfterEmail(state.personalDetails, token);
                }}
                onSkip={() => {
                  void goToAfterEmail(state.personalDetails, undefined);
                }}
                onBack={() => {
                  setState({ step: "personal-details" });
                }}
              />
            )}
            {state.step === "billing-info" && !isEur && (
              <BillingForm
                onNext={async (data) => {
                  await showModal(<UsernameRecommendationModal />);

                  setState({
                    step: "auth-and-contracts",
                    personalDetails: state.personalDetails,
                    billingInfo: data.billingInfo as
                      | PostBillingInfoType
                      | undefined,
                    emailVerificationToken: state.emailVerificationToken,
                  });
                }}
                onBack={() => {
                  setState(
                    emailVerificationEnabled
                      ? {
                          step: "email-verification",
                          personalDetails: state.personalDetails,
                        }
                      : { step: "personal-details" },
                  );
                }}
                personalDetails={state.personalDetails}
              />
            )}
            {state.step === "auth-and-contracts" && (
              <AuthForm
                onSuccess={() => {
                  // Goes on to machine selection unless the welcome coupon is on offer.
                  navigate({ to: "/welcome-coupon-screen" });
                }}
                onBack={() => {
                  if (state.step === "auth-and-contracts") {
                    if (isEur) {
                      setState(
                        emailVerificationEnabled
                          ? {
                              step: "email-verification",
                              personalDetails: state.personalDetails,
                            }
                          : { step: "personal-details" },
                      );
                    } else {
                      setState({
                        step: "billing-info",
                        personalDetails: state.personalDetails,
                        emailVerificationToken: state.emailVerificationToken,
                      });
                    }
                  }
                }}
                personalDetails={state.personalDetails}
                billingInfo={state.billingInfo}
                recommendedUsername={state.personalDetails.recommendedUsername}
                emailVerificationToken={state.emailVerificationToken}
              />
            )}
          </Suspense>
        </div>
      </PageContent>
      <Footer
        helpText={
          <Trans>
            A regisztrációhoz add meg vezetékneved, keresztneved, születési
            évedet és e-mail címed. Az adatok megadása kötelező.
          </Trans>
        }
      />
    </Page>
  );
}

const getCompletedSteps = (
  step: Step,
  flags: { emailVerificationEnabled: boolean; isEur: boolean },
): string[] => {
  const order = [
    "personal-details",
    ...(flags.emailVerificationEnabled ? ["email-verification"] : []),
    ...(!flags.isEur ? ["billing-info"] : []),
    "auth-and-contracts",
  ];
  const idx = order.indexOf(step);
  return order.slice(0, Math.max(idx, 0));
};

export function UsernameRecommendationModal() {
  useAudioSlot("modal.username-recommendation");
  const { closeModal } = useMuiModal();
  return (
    <Modal>
      <ModalTitle>
        <Trans>Ajánlott felhasználónév</Trans>
      </ModalTitle>
      <ModalContent>
        <div className="gap-y-8 p-6">
          <p className="text-center">
            <Trans>
              A rendszer automatikusan felajánl neked egy felhasználónevet. Ha
              megfelelő számodra csak pipáld ki a mellette látható négyzetet. Ha
              szeretnél másikat megadni kérjük, írj be egyet a Felhasználónév
              mezőbe!
            </Trans>
          </p>

          <div className="flex gap-2 justify-center">
            <Button onClick={closeModal}>OK</Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}
